const ANNOTATE_STROKE = "oklch(0.546 0.215 262)";

export type DrawTool = "pen" | "eraser";

export type StrokePoint = { x: number; y: number };
type Stroke = StrokePoint[];

export type DrawController = {
  isDrawing: () => boolean;
  hasDrawing: () => boolean;
  setEnabled: (on: boolean) => void;
  setTool: (tool: DrawTool) => void;
  /** Copy of the strokes drawn since the last session end (page coordinates). */
  snapshotSession: () => StrokePoint[][];
  /** Remove exactly these strokes (they moved into a collected entry). */
  removeStrokes: (strokes: StrokePoint[][]) => void;
  /** Drop every stroke of the current session - its card closed unsent. */
  discardSession: () => void;
  /** Ghost-render an entry's saved page-space strokes (null clears them). */
  setOverlay: (strokes: StrokePoint[][] | null) => void;
  clear: () => void;
};

export type DrawHandlers = {
  onEscape: () => void;
  surfaceRoot: HTMLElement;
  /** Fired when a stroke with real length is released, with its end point in
      viewport coordinates. */
  onStrokeEnd?: (point: { x: number; y: number }) => void;
};

/** Max distance (px) from a stroke segment for the eraser to catch it. */
const ERASE_THRESHOLD = 12;

const distanceToSegment = (px: number, py: number, a: StrokePoint, b: StrokePoint): number => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSq = dx * dx + dy * dy;
  const t =
    lengthSq === 0 ? 0 : Math.max(0, Math.min(1, ((px - a.x) * dx + (py - a.y) * dy) / lengthSq));
  return Math.hypot(px - (a.x + t * dx), py - (a.y + t * dy));
};

const strokeHit = (stroke: Stroke, x: number, y: number): boolean => {
  if (stroke.length === 1) return Math.hypot(x - stroke[0]!.x, y - stroke[0]!.y) < ERASE_THRESHOLD;
  for (let i = 0; i < stroke.length - 1; i++) {
    if (distanceToSegment(x, y, stroke[i]!, stroke[i + 1]!) < ERASE_THRESHOLD) return true;
  }
  return false;
};

export function createDrawController(handlers: DrawHandlers): DrawController {
  const { onEscape } = handlers;
  let drawing = false;
  let activePointerId: number | null = null;
  /** All strokes, in page coordinates so they stay glued through scrolling. */
  let strokes: Stroke[] = [];
  /** Strokes belonging to the open (or next) annotation card, by reference -
      immune to erase-driven reindexing. */
  let session = new Set<Stroke>();
  let overlay: StrokePoint[][] | null = null;
  let currentStroke: Stroke | null = null;
  let canvas: HTMLCanvasElement | null = null;
  let ctx: CanvasRenderingContext2D | null = null;
  let ghostCanvas: HTMLCanvasElement | null = null;
  let ghostCtx: CanvasRenderingContext2D | null = null;
  let enabled = false;
  let tool: DrawTool = "pen";
  let rafId: number | null = null;
  let repaintQueued = false;
  let paintedPointCount = 0;
  let scrollBound = false;

  const pageX = (e: PointerEvent): number => e.clientX + window.scrollX;
  const pageY = (e: PointerEvent): number => e.clientY + window.scrollY;

  const paintStroke = (target: CanvasRenderingContext2D, stroke: Stroke): void => {
    if (stroke.length < 2) return;
    target.beginPath();
    target.moveTo(stroke[0]!.x - window.scrollX, stroke[0]!.y - window.scrollY);
    for (let i = 1; i < stroke.length; i++) {
      target.lineTo(stroke[i]!.x - window.scrollX, stroke[i]!.y - window.scrollY);
    }
    target.stroke();
  };

  const wipe = (target: CanvasRenderingContext2D, surface: HTMLCanvasElement): void => {
    target.save();
    target.setTransform(1, 0, 0, 1, 0, 0);
    target.clearRect(0, 0, surface.width, surface.height);
    target.restore();
  };

  const repaintAll = (): void => {
    if (ctx && canvas) {
      wipe(ctx, canvas);
      for (const stroke of strokes) paintStroke(ctx, stroke);
    }
    // Ghosts live on their own canvas so screenshots (which composite only
    // the real draw canvas) can never inherit another entry's strokes.
    if (ghostCtx && ghostCanvas) {
      wipe(ghostCtx, ghostCanvas);
      if (overlay) for (const stroke of overlay) paintStroke(ghostCtx, stroke);
    }
  };

  const scheduleRepaint = (): void => {
    if (repaintQueued) return;
    repaintQueued = true;
    requestAnimationFrame(() => {
      repaintQueued = false;
      repaintAll();
    });
  };

  const paintPendingSegments = (): void => {
    if (!ctx || !currentStroke) return;
    while (paintedPointCount < currentStroke.length - 1) {
      const prev = currentStroke[paintedPointCount]!;
      const next = currentStroke[paintedPointCount + 1]!;
      paintStroke(ctx, [prev, next]);
      paintedPointCount += 1;
    }
  };

  const schedulePaint = (): void => {
    if (rafId !== null) return;
    rafId = requestAnimationFrame(() => {
      rafId = null;
      paintPendingSegments();
    });
  };

  const cancelScheduledPaint = (): void => {
    if (rafId === null) return;
    cancelAnimationFrame(rafId);
    rafId = null;
  };

  const flushPaint = (): void => {
    cancelScheduledPaint();
    paintPendingSegments();
  };

  const onWindowScroll = (): void => {
    if (strokes.length > 0 || overlay) scheduleRepaint();
  };

  const CANVAS_CSS =
    // Below the widget layer: annotation cards must stay clickable over it.
    "position:fixed;inset:0;width:100vw;height:100vh;z-index:2147482990;pointer-events:none;touch-action:none;";

  const makeSurface = (attr: string): [HTMLCanvasElement, CanvasRenderingContext2D | null] => {
    const c = document.createElement("canvas");
    c.setAttribute(attr, "");
    c.style.cssText = CANVAS_CSS;
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.floor(window.innerWidth * dpr);
    c.height = Math.floor(window.innerHeight * dpr);
    const context = c.getContext("2d");
    if (context) {
      context.scale(dpr, dpr);
      context.strokeStyle = ANNOTATE_STROKE;
      context.lineWidth = 3;
      context.lineCap = "round";
      context.lineJoin = "round";
    }
    handlers.surfaceRoot.appendChild(c);
    return [c, context];
  };

  const ensureCanvas = (): HTMLCanvasElement => {
    if (canvas) return canvas;
    [canvas, ctx] = makeSurface("data-pl-draw");
    if (!scrollBound) {
      scrollBound = true;
      window.addEventListener("scroll", onWindowScroll, { passive: true });
    }
    return canvas;
  };

  const ensureGhostCanvas = (): void => {
    if (ghostCanvas) return;
    [ghostCanvas, ghostCtx] = makeSurface("data-pl-draw-ghost");
  };

  const eraseAt = (x: number, y: number): void => {
    const px = x + window.scrollX;
    const py = y + window.scrollY;
    const remaining = strokes.filter((stroke) => {
      const hit = strokeHit(stroke, px, py);
      if (hit) session.delete(stroke);
      return !hit;
    });
    if (remaining.length === strokes.length) return;
    strokes = remaining;
    scheduleRepaint();
  };

  const onDrawStart = (e: PointerEvent): void => {
    if (activePointerId !== null) return;
    activePointerId = e.pointerId;
    drawing = true;
    if (tool === "eraser") {
      eraseAt(e.clientX, e.clientY);
      return;
    }
    currentStroke = [{ x: pageX(e), y: pageY(e) }];
    strokes.push(currentStroke);
    session.add(currentStroke);
    paintedPointCount = 0;
  };

  const onDrawMove = (e: PointerEvent): void => {
    if (!drawing || e.pointerId !== activePointerId) return;
    if (tool === "eraser") {
      eraseAt(e.clientX, e.clientY);
      return;
    }
    if (!currentStroke || currentStroke.length === 0) return;
    currentStroke.push({ x: pageX(e), y: pageY(e) });
    schedulePaint();
  };

  const resetActivePointer = (): void => {
    drawing = false;
    activePointerId = null;
    currentStroke = null;
    paintedPointCount = 0;
  };

  const onDrawEnd = (e: PointerEvent): void => {
    if (e.pointerId !== activePointerId) return;
    flushPaint();
    const stroke = currentStroke;
    resetActivePointer();
    if (tool === "pen" && stroke && stroke.length > 1) {
      const end = stroke[stroke.length - 1]!;
      handlers.onStrokeEnd?.({
        x: end.x - window.scrollX,
        y: end.y - window.scrollY,
      });
    }
  };

  const onDrawCancel = (e: PointerEvent): void => {
    if (e.pointerId !== activePointerId) return;
    cancelScheduledPaint();
    const canceledStroke = currentStroke;
    resetActivePointer();
    if (!canceledStroke) return;
    strokes = strokes.filter((stroke) => stroke !== canceledStroke);
    session.delete(canceledStroke);
    repaintAll();
  };

  const onDrawKeydown = (e: KeyboardEvent): void => {
    if (e.key === "Escape") onEscape();
  };

  const bind = (on: boolean): void => {
    const c = ensureCanvas();
    if (on) {
      c.style.pointerEvents = "auto";
      c.style.cursor = tool === "eraser" ? "cell" : "crosshair";
      c.addEventListener("pointerdown", onDrawStart);
      window.addEventListener("pointermove", onDrawMove, true);
      window.addEventListener("pointerup", onDrawEnd, true);
      window.addEventListener("pointercancel", onDrawCancel, true);
      document.addEventListener("keydown", onDrawKeydown, true);
    } else {
      cancelScheduledPaint();
      resetActivePointer();
      c.style.pointerEvents = "none";
      c.style.cursor = "";
      c.removeEventListener("pointerdown", onDrawStart);
      window.removeEventListener("pointermove", onDrawMove, true);
      window.removeEventListener("pointerup", onDrawEnd, true);
      window.removeEventListener("pointercancel", onDrawCancel, true);
      document.removeEventListener("keydown", onDrawKeydown, true);
    }
  };

  return {
    isDrawing: () => enabled,
    hasDrawing: () => strokes.length > 0,
    snapshotSession: () => [...session].map((stroke) => stroke.map((p) => ({ ...p }))),
    removeStrokes: (toRemove) => {
      if (toRemove.length === 0) return;
      // Snapshots are copies; match session strokes by shape (length + ends).
      const keys = new Set(
        toRemove.map(
          (s) => `${s.length}:${s[0]?.x},${s[0]?.y}:${s[s.length - 1]?.x},${s[s.length - 1]?.y}`,
        ),
      );
      strokes = strokes.filter((stroke) => {
        const key = `${stroke.length}:${stroke[0]?.x},${stroke[0]?.y}:${stroke[stroke.length - 1]?.x},${stroke[stroke.length - 1]?.y}`;
        const drop = session.has(stroke) && keys.has(key);
        if (drop) session.delete(stroke);
        return !drop;
      });
      repaintAll();
    },
    discardSession: () => {
      if (session.size === 0) return;
      strokes = strokes.filter((stroke) => !session.has(stroke));
      session = new Set();
      repaintAll();
    },
    setOverlay: (next) => {
      if (next === null && overlay === null) return;
      overlay = next;
      ensureGhostCanvas();
      if (!scrollBound) {
        scrollBound = true;
        window.addEventListener("scroll", onWindowScroll, { passive: true });
      }
      repaintAll();
    },
    setTool: (next) => {
      tool = next;
      if (canvas && enabled) canvas.style.cursor = next === "eraser" ? "cell" : "crosshair";
    },
    setEnabled: (on: boolean) => {
      if (on === enabled) return;
      enabled = on;
      bind(on);
    },
    clear: () => {
      strokes = [];
      session = new Set();
      currentStroke = null;
      repaintAll();
    },
  };
}
